import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';

interface FormDialogProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  onSubmit?: () => void;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  submitText?: string;
  cancelText?: string;
  isSubmitting?: boolean;
  disableSubmit?: boolean;
  showActions?: boolean;
}

const FormDialog: React.FC<FormDialogProps> = ({
  open,
  title,
  subtitle,
  onClose,
  onSubmit,
  children,
  maxWidth = 'sm',
  submitText = 'Submit',
  cancelText = 'Cancel',
  isSubmitting = false,
  disableSubmit = false,
  showActions = true,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          disabled={isSubmitting}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 3 }}>
        {children}
      </DialogContent>
      {showActions && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ textTransform: 'none' }}
          >
            {cancelText}
          </Button>
          <Button
            onClick={onSubmit}
            variant="contained"
            disabled={disableSubmit || isSubmitting}
            sx={{
              textTransform: 'none',
              px: 3,
            }}
          >
            {isSubmitting ? 'Submitting...' : submitText}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default FormDialog;
